import { useEffect, useState } from 'react';
import { UsersIcon } from 'lucide-react';
import type { AgendaEvent, AgendaRegistration } from '@kennisbank/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { apiDelete, apiGet, apiPost } from '../api/client.js';

const INPUT_CLS =
  'w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400';

/**
 * Aanmelden voor jezelf, plus de deelnemerslijst voor wie de aanmeldingen
 * bijhoudt (besluit 6 augustus 2026: lijst blijft bij het agenda-item, maar
 * alleen voor kantoorpersoneel en beheerders). Een gewone medewerker ziet
 * alleen het aantal; de backend stuurt hem de namen van collega's niet eens.
 */
export function AgendaRegistrations({
  event,
  onGewijzigd,
}: {
  event: AgendaEvent;
  onGewijzigd?: () => void;
}): JSX.Element {
  const { user } = useAuth();
  const magLijstZien =
    user?.role === 'suppohrt_admin' ||
    user?.isHeadOffice === true ||
    event.organisatoren?.some((o) => o.userId === user?.id) === true;
  const [registrations, setRegistrations] = useState<AgendaRegistration[] | null>(null);
  const [count, setCount] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void apiGet<{ registrations: AgendaRegistration[]; count: number }>(
      `/api/v1/agenda/${event.id}/aanmeldingen`,
    )
      .then((d) => {
        setRegistrations(d.registrations);
        setCount(d.count);
      })
      .catch(() => setError('Kon de aanmeldingen niet laden.'));
  }, [event.id]);

  const mine = registrations?.find((r) => r.userId === user?.id);

  async function toggle(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      if (mine) {
        await apiDelete(`/api/v1/agenda/${event.id}/aanmeldingen`);
        setRegistrations((prev) => (prev ?? []).filter((r) => r.userId !== user?.id));
        setCount((n) => Math.max(0, n - 1));
      } else {
        const { registration } = await apiPost<{ registration: AgendaRegistration }>(
          `/api/v1/agenda/${event.id}/aanmeldingen`,
          { note: note.trim() || undefined },
        );
        setRegistrations((prev) => [...(prev ?? []), registration]);
        setCount((n) => n + 1);
        setNote('');
      }
      onGewijzigd?.();
    } catch {
      setError('Dat lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 rounded-xl border border-brand-100 bg-brand-50/40 p-4">
      <div className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-ink-900">
        <UsersIcon size={14} className="text-brand-600" />
        Aanmeldingen
        {registrations !== null && <span className="font-normal text-ink-900/50">({count})</span>}
      </div>

      {registrations === null ? (
        <p className="mb-3 text-[12px] text-ink-900/40">Laden…</p>
      ) : magLijstZien && registrations.length > 0 ? (
        <ul className="mb-3 flex flex-col gap-1.5">
          {[...registrations]
            .sort((a, b) => a.displayName.localeCompare(b.displayName, 'nl'))
            .map((r) => (
              <li key={r.userId} className="text-[12px] text-ink-900/70">
                <span className="font-medium text-ink-900">{r.displayName}</span>
                {(r.lidnummerLvpoh || r.lidnummerSkj) && (
                  <span className="text-ink-900/55">
                    {' '}
                    ·{' '}
                    {[
                      r.lidnummerLvpoh && `LVPOH ${r.lidnummerLvpoh}`,
                      r.lidnummerSkj && `SKJ ${r.lidnummerSkj}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
                {r.note && <span className="block text-ink-900/60">{r.note}</span>}
              </li>
            ))}
        </ul>
      ) : (
        <p className="mb-3 text-[12px] text-ink-900/70">
          {mine
            ? 'Je bent aangemeld.'
            : count === 0
              ? 'Nog niemand aangemeld.'
              : count === 1
                ? 'Er is één aanmelding.'
                : `Er zijn ${count} aanmeldingen.`}
          {mine?.note && <span className="block text-ink-900/60">Jouw opmerking: {mine.note}</span>}
        </p>
      )}

      {!mine && (
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Opmerking (optioneel)"
          maxLength={1000}
          className={`${INPUT_CLS} mb-2 bg-white`}
        />
      )}

      <button
        type="button"
        onClick={() => void toggle()}
        disabled={busy || registrations === null}
        className={
          mine
            ? 'rounded-xl px-4 py-2 text-[13px] font-medium text-ink-900/60 hover:bg-white transition-colors disabled:opacity-50'
            : 'rounded-xl bg-brand-600 px-5 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 transition-colors disabled:opacity-50'
        }
      >
        {busy ? 'Bezig…' : mine ? 'Toch niet, meld mij af' : 'Ik ben erbij!'}
      </button>

      {error && <p className="mt-2 text-[12px] text-error">{error}</p>}
    </div>
  );
}
